/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import createEntityObserver from './createEntityObserver';
import {EntitySubscription} from './types';

const jobStatus = createEntityObserver(EntitySubscription.jobStatus);

/**
 * The JobStatusObserver API allows for receiving a notification once a Job, or
 * collection of Jobs, has reached a terminal status (Cancelled, Failed, or
 * Completed). This API essentially delegates to the EntityObserver API, only
 * in the context of Job status only.
 *
 * Note, while initialization of the EntityObserver is generally performed during
 * application initialization, as a best practice, always add observers via the
 * EntityObserver.onInitialized method, which will invoke the provided callback
 * once initialized, or immediately if already initialized:
 *
 * ```typescript
 *
 *   import EntityObserver from '@ion/services/src/websocket/EntityObserver';
 *   import JobStatusObserver from '@ion/services/src/websocket/JobStatusObserver';
 *
 *   EntityObserver.onInitialized(() => {
 *     JobStatusObserver.addObserver(job.id, onJobStatus);
 *   });
 *
 * ```
 * Once a Job (or collection of Jobs) no longer need to be observed, make sure
 * to invoke removeObserver ...
 *
 * ```typescript
 *
 *   JobStatusObserver.removeObserver(job.id, onJobStatus);
 *
 * ```
 *
 * @see JobProgressObserver
 */
export default class JobStatusObserver {
  /**
   * The addObserver method allows for observing a Job or collection of Jobs
   * for having reached a terminal status.
   *
   * ```typescript
   *
   *   import EntityObserver from '@ion/services/src/websocket/EntityObserver';
   *   import JobStatusObserver from '@ion/services/src/websocket/JobStatusObserver';
   *
   *   EntityObserver.onInitialized(() => {
   *     JobStatusObserver.addObserver(job.id, onJobStatus);
   *   });
   *
   * ```
   * @param   ids Either a single id, or array of job ids to be observed for
   *          changes.
   * @param   observer Callback function which will be invoked upon receiving
   *          changes for the observed jobs.
   * @returns true if the observer has been added, otherwise, false. Generally,
   *          an observer will only ever fail to be added if the connection
   *          has been closed or has not yet been established.
   */
  static addObserver = jobStatus.addObserver;

  /**
   * The removeObserver method allows for removing a Job or collection of Jobs
   * from being observed for having reached a terminal status. This method must
   * always be invoked when an observer is no longer needed.
   *
   * ```typescript
   *
   *   import JobStatusObserver from '@ion/services/src/websocket/JobStatusObserver';
   *
   *   JobStatusObserver.removeObserver(job.id, onJobStatus);
   *
   * ```
   *
   * @param   ids Either a single id, or array of job ids to no longer be
   *          observed for changes.
   * @param   observer The callback function previously passed to `addObserver`
   *          for the given ids.
   * @returns true if the observer has been removed, otherwise, false. Generally,
   *          an observer will only ever fail to be removed if the connection
   *          has been closed or has not yet been established.
   */
  static removeObserver = jobStatus.removeObserver;

  /**
   * Provides a utility which allows for removing all job status observers
   * within an application, leaving the observers of every other entity type,
   * including those of the job progress channel, intact.
   *
   * ```typescript
   *
   *   import JobStatusObserver from '@ion/services/src/websocket/JobStatusObserver';
   *
   *   // unsubscribe and remove all jobs from being observed for status ...
   *   JobStatusObserver.removeAllObservers();
   *
   * ```
   *
   * @returns true if the observers have been removed, otherwise, false.
   *          Generally, an observer will only ever fail to be removed if the
   *          connection has been closed or has not yet been established.
   */
  static removeAllObservers = jobStatus.removeAllObservers;
}
